import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import VerticalTabs from 'components/VerticalTabs';
import { useAsyncFn } from 'react-use';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ROLES } from 'appConstants';
import { getTrainingSession } from 'http/trainingSessions';
import { routesAliases } from 'router/routesAliases';
import { isCancelledStatus } from 'utils/entityLifecycleStatus';
import TrainingSessionForm from 'pages/TrainingSessions/TrainingSessionForm';
import ParticipantsPanel from 'pages/TrainingSessions/Edit/ParticipantsPanel';
import SessionFilesPanel from 'pages/TrainingSessions/Edit/SessionFilesPanel';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
  summary: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const ViewTrainingSession = ({ routeProps }) => {
  const classes = useStyles();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const { id } = routerMatch.params;
  const role = useSelector(state => state.auth.profile?.role);
  const isStudent = role === ROLES.USER;
  const [notFoundError, setNotFoundError] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const moduleLabel = useMemo(() => {
    if (!sessionData) {
      return '';
    }
    const code = sessionData.trainingModuleCode || '';
    const name = sessionData.trainingModuleName || '';
    if (code && name) {
      return `${code}. ${name}`;
    }
    return name || code;
  }, [sessionData]);

  const sessionTitle = useMemo(() => {
    if (!sessionData) {
      return t(routeProps.title);
    }
    const partVersion = sessionData.trainingPartVersionCode || '';
    const methodLabel = sessionData.trainingMethod
      ? t(`trainingModules.tabs.${sessionData.trainingMethod}`, {
          defaultValue: sessionData.trainingMethod,
        })
      : '';
    const base = `${moduleLabel} ${partVersion}`.trim();
    return methodLabel ? `${base} (${methodLabel})`.trim() : base || t(routeProps.title);
  }, [moduleLabel, routeProps.title, sessionData, t]);

  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: sessionTitle,
      isActive: true,
    },
  ];

  const [fetchState, fetchSession] = useAsyncFn(async sessionId => {
    const result = await getTrainingSession(sessionId);
    if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
      setSessionData(result);
      setNotFoundError(false);
    } else if (result.statusCode === 404 || result.statusCode === 403) {
      setNotFoundError(true);
    }
    return result;
  }, []);

  useEffect(() => {
    setNotFoundError(false);
    setSessionData(null);
    fetchSession(id);
  }, [id, fetchSession]);

  const isCancelled = isCancelledStatus(sessionData?.status);

  const tabsData = useMemo(() => {
    if (!sessionData) {
      return [];
    }

    const tabs = [
      {
        label: t('trainingSessions.tabs.general'),
        alias: 'general',
        Component: (
          <TrainingSessionForm
            mode="edit"
            initialValues={sessionData}
            onSubmitForm={async () => ({})}
            backUrl={routesAliases.myTrainingSessionsList}
            readOnly
          />
        ),
      },
    ];

    if (!isStudent) {
      tabs.push({
        label: t('trainingSessions.tabs.participants'),
        alias: 'participants',
        Component: (
          <ParticipantsPanel
            trainingSessionId={id}
            deliveryMethod={sessionData.deliveryMethod}
            hasMoodleLink={sessionData.hasMoodleLink}
            moodleLmsCourseId={sessionData.moodleLmsCourseId}
            trainingMethod={sessionData.trainingMethod}
            readOnly={isCancelled}
            canAssign={false}
            forTrainer
          />
        ),
      });
    }

    tabs.push({
      label: t('trainingSessions.tabs.files'),
      alias: 'files',
      Component: (
        <SessionFilesPanel
          trainingSessionId={sessionData.id}
          trainingPartId={sessionData.trainingPartId}
          canManageVisibility={false}
          isReadOnly
        />
      ),
    });

    return tabs;
  }, [id, isCancelled, isStudent, sessionData, t]);

  if (notFoundError) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !sessionData}
    >
      {sessionData ? (
        <>
          <Box className={classes.summary}>
            <Typography variant="body2">
              {t('trainingSessions.summary', {
                module: moduleLabel,
                part: sessionData.trainingMethod
                  ? `${sessionData.trainingPartVersionCode || ''} (${t(
                      `trainingModules.tabs.${sessionData.trainingMethod}`,
                      {
                        defaultValue: sessionData.trainingMethod,
                      },
                    )})`.trim()
                  : sessionData.trainingPartVersionCode,
                method: t(`trainingSessions.deliveryMethods.${sessionData.deliveryMethod}`),
              })}
              {sessionData.status
                ? ` - ${t(`trainingSessions.statuses.${sessionData.status}`, {
                    defaultValue: sessionData.status,
                  })}`
                : ''}
            </Typography>
          </Box>
          <VerticalTabs
            tabsData={tabsData}
            activeTab={activeTab}
            onTabChange={(event, { tabIndex }) => setActiveTab(tabIndex)}
          />
        </>
      ) : null}
    </PageLayout>
  );
};

ViewTrainingSession.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default ViewTrainingSession;
